// stlUtils.js
// Utilidades STL profesionales: carga, limpieza y exportación
// Dependencias: three, three/examples/jsm/loaders/STLLoader, STLExporter, BufferGeometryUtils

import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils";

/**
 * Carga un archivo STL y retorna un BufferGeometry normalizado y limpio.
 * @param {File|Blob} file - Archivo STL a cargar.
 * @returns {Promise<THREE.BufferGeometry>}
 */
export async function loadSTL(file) {
  return new Promise((resolve, reject) => {
    try {
      const loader = new STLLoader();
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const geometry = loader.parse(e.target.result);
          const cleaned = cleanGeometry(geometry);
          resolve(cleaned);
        } catch (err) {
          console.error("❌ Error al parsear STL:", err);
          reject(new Error("No se pudo procesar el archivo STL. Asegúrate de que no esté dañado."));
        }
      };

      reader.onerror = () => {
        reject(new Error("Error al leer el archivo STL."));
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Limpia y normaliza una geometría STL.
 * - Elimina duplicados.
 * - Centra en el origen.
 * - Recalcula normales.
 * - Convierte a BufferGeometry si no lo es.
 * @param {THREE.BufferGeometry|THREE.Geometry} geometry
 * @returns {THREE.BufferGeometry}
 */
export function cleanGeometry(geometry) {
  let geom = geometry.clone();

  // Convertir a BufferGeometry si es necesario
  if (!(geom instanceof THREE.BufferGeometry)) {
    geom = new THREE.BufferGeometry().fromGeometry(geom);
  }

  // Eliminar vértices duplicados
  try {
    geom = BufferGeometryUtils.mergeVertices(geom);
  } catch (err) {
    console.warn("⚠️ No se pudieron fusionar vértices:", err);
  }

  // Recalcular normales
  geom.computeVertexNormals();
  geom.computeBoundingBox();

  // Centrar
  const box = geom.boundingBox;
  if (box) {
    const center = new THREE.Vector3();
    box.getCenter(center);
    geom.translate(-center.x, -center.y, -center.z);
  }

  return geom;
}

/**
 * Exporta una geometría STL como Blob listo para descargar.
 * @param {THREE.BufferGeometry} geometry - Geometría a exportar.
 * @param {Object} [options]
 * @param {boolean} [options.binary=false] - Si es true, exporta en formato binario.
 * @returns {Blob}
 */
export function exportSTL(geometry, options = {}) {
  const exporter = new STLExporter();
  const { binary = false } = options;

  try {
    const data = exporter.parse(geometry, { binary });
    return new Blob([data], { type: "application/sla" });
  } catch (err) {
    console.error("❌ Error al exportar STL:", err);
    throw new Error("No se pudo exportar la geometría STL.");
  }
}

/**
 * Descarga un Blob STL directamente al usuario.
 * @param {Blob} blob
 * @param {string} filename
 */
export function downloadSTL(blob, filename = "modelo_reparado.stl") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Verifica si un archivo es STL por extensión o tipo MIME.
 * @param {File} file
 * @returns {boolean}
 */
export function isSTLFile(file) {
  const name = file.name?.toLowerCase() || "";
  const type = file.type?.toLowerCase() || "";
  return name.endsWith(".stl") || type.includes("stl");
}
